import { Checkbox, FormControlLabel, Paper, TextField, Typography } from '@mui/material';
import React from 'react';

import { ProgramCatalogItem } from '../../api/programs.interface';
import { buildProgramContentPath, buildProgramSchedulePath } from './programs.utils';

interface CatalogErrors {
    displayName?: string;
    weeks?: string;
}

interface Props {
    disabledKey?: boolean;
    errors?: CatalogErrors;
    hideProgramKey?: boolean;
    keyError?: string;
    onCatalogBooleanChange: (fieldName: keyof Pick<ProgramCatalogItem, 'live'>, value: boolean) => void;
    onCatalogFieldChange: (fieldName: keyof Omit<ProgramCatalogItem, 'live' | 'weeks'>, value: string) => void;
    onCatalogWeeksChange: (value: string) => void;
    onProgramKeyChange?: (value: string) => void;
    programCatalog: ProgramCatalogItem;
    programKey: string;
    title?: string;
}

const ProgramCatalogFields: React.FC<Props> = ({
    disabledKey,
    errors,
    hideProgramKey,
    keyError,
    onCatalogBooleanChange,
    onCatalogFieldChange,
    onCatalogWeeksChange,
    onProgramKeyChange,
    programCatalog,
    programKey,
    title = 'Program Metadata',
}) => {
    const displayNameError = errors?.displayName || (hideProgramKey ? keyError : undefined);
    const displayNameHelperText = displayNameError || (hideProgramKey ? 'Program key will be generated from the display name.' : undefined);

    return (
        <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
            <Typography sx={{ fontWeight: 700, mb: 1.5 }} variant="subtitle1">{title}</Typography>
            {!hideProgramKey && (
                <TextField
                    disabled={disabledKey}
                    error={Boolean(keyError)}
                    fullWidth
                    helperText={keyError || 'Stable route and API identifier.'}
                    label="Program Key"
                    onChange={(event) => onProgramKeyChange?.(event.target.value)}
                    size="small"
                    sx={{ mb: 1.5 }}
                    value={programKey}
                />
            )}
            <TextField
                error={Boolean(displayNameError)}
                fullWidth
                helperText={displayNameHelperText}
                label="Display Name"
                onChange={(event) => onCatalogFieldChange('displayName', event.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
                value={programCatalog.displayName}
            />
            <Typography color="text.secondary" sx={{ mb: 0.5 }} variant="body2">Programs path: {buildProgramContentPath(programKey || 'program')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">Schedule path: {buildProgramSchedulePath(programKey || 'program')}</Typography>
            <TextField
                error={Boolean(errors?.weeks)}
                fullWidth
                helperText={errors?.weeks || 'Used by list completeness and workout processing.'}
                label="Configured Weeks"
                onChange={(event) => onCatalogWeeksChange(event.target.value)}
                size="small"
                sx={{ mb: 1 }}
                value={String(programCatalog.weeks)}
            />
            <FormControlLabel
                control={<Checkbox checked={programCatalog.live} onChange={(event) => onCatalogBooleanChange('live', event.target.checked)} />}
                label="Live Program"
            />
        </Paper>
    );
};

export default ProgramCatalogFields;